import { useSchedulerContext } from '@contexts/scheduler';
import { SchedulerGrid } from './grid';
import { SchedulerProps } from './types';
import { TouchEvent, useEffect, useMemo, useRef } from 'react';
import { setDatesForCurrentDay } from '@contexts/scheduler/helpers';
import { getDividedHours, getFirstHour, getHours } from './grid/helpers';
import { useScheduler } from '@hooks/scheduler';
import { parseISO } from 'date-fns';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Modifier,
} from '@dnd-kit/core';
import { getSegmentFromPoint } from '@hooks/scheduler/dnd/helpers';
import { restrictToBoundingRect } from '@utils/dnd/restrictToBoundingRect';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';

export default function Scheduler({
  date,
  foremen,
  withoutTeam,
  outOfScopeRecords,
  modal,
  inventory,
  schedule,
  activeId,
  restrictTo = 'window',
}: SchedulerProps) {
  const {
    date: contextDate,
    data: { raw },
    segment,
    dayStart,
    hoursCount,
    active,
    setState,
  } = useSchedulerContext();

  const { getSchedule, setSchedule } = useScheduler();

  useEffect(() => {
    if (!foremen || !withoutTeam) return;
    setState((prev) => ({
      ...prev,
      active: {
        ...prev.active,
        fresh: false,
      },
      outOfScopeRecords: Boolean(outOfScopeRecords),
      showFullDay: prev.showFullDayOverridden ? prev.showFullDay : Boolean(outOfScopeRecords),
      data: {
        ...prev.data,
        raw: { foremen, withoutTeam },
      },
    }));
  }, [foremen, withoutTeam, outOfScopeRecords, setState]);

  useEffect(() => {
    if (!date) return;
    setState((prev) => ({ ...prev, date: getFirstHour(date, dayStart) }));
  }, [date, dayStart, setState]);

  useEffect(() => {
    if (!contextDate) return;
    setState((prev) => ({
      ...prev,
      hours: getDividedHours(getHours(getFirstHour(contextDate, dayStart), hoursCount), segment),
    }));
  }, [contextDate, dayStart, segment, hoursCount, setState]);

  useEffect(() => {
    if (!modal) return;
    setState((prev) => ({ ...prev, modal }));
  }, [modal, setState]);

  useEffect(() => {
    if (!inventory) return;
    setState((prev) => ({ ...prev, inventory }));
  }, [inventory, setState]);

  useEffect(() => {
    const storedSchedule = active.schedule.id
      ? active.schedule
      : getSchedule(
          { foremen: raw.foremen || [], withoutTeam: raw.withoutTeam || [] },
          schedule?.id,
          false,
        ) || schedule;
    if (!storedSchedule || !storedSchedule.startsAt || !storedSchedule.endsAt || active?.fresh)
      return;
    if (activeId && storedSchedule?.id !== activeId) return;
    setState((prev) => ({
      ...prev,
      active: {
        ...prev.active,
        fresh: false,
        schedule: {
          ...storedSchedule,
          task: schedule?.task || storedSchedule.task,
          ...setDatesForCurrentDay(contextDate, storedSchedule.startsAt, storedSchedule.endsAt),
        },
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule, raw, contextDate, setState, activeId]);

  useEffect(() => {
    if (!active.schedule?.id || active?.fresh) return;
    const { startsAt, endsAt } = setDatesForCurrentDay(
      contextDate,
      active.schedule.startsAt,
      active.schedule.endsAt,
    );
    getSchedule({ foremen: foremen || [], withoutTeam: withoutTeam || [] }, active.schedule.id);
    setSchedule(
      { ...active.schedule, task: schedule?.task || active.schedule.task, startsAt, endsAt },
      typeof active.newValues.foreman?.id === 'string'
        ? active.newValues.foreman.id
        : active.schedule.foreman?.id || '',
    );
    setState((prev) => ({
      ...prev,
      active: {
        ...prev.active,
        newValues: {
          ...prev.active.newValues,
          startsAt: parseISO(startsAt),
          endsAt: parseISO(endsAt),
        },
        schedule: {
          ...prev.active.schedule,
          foreman: prev.active.newValues.foreman || prev.active.schedule.foreman,
          task: schedule?.task || active.schedule.task,
        },
        fresh: true,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.schedule, active.fresh, foremen, withoutTeam]);

  const mouseSensor = useSensor(MouseSensor, {
    onActivation: ({ event }) => {
      const { clientX, clientY } = event as unknown as MouseEvent;

      setState((prev) => ({
        ...prev,
        active: {
          ...prev.active,
          newValues: { ...prev.active.newValues, from: getSegmentFromPoint(clientX, clientY) },
        },
      }));
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    onActivation: ({ event }) => {
      const { touches } = event as unknown as TouchEvent;
      const { clientX, clientY } = touches[0];

      setState((prev) => ({
        ...prev,
        active: {
          ...prev.active,
          newValues: { ...prev.active.newValues, from: getSegmentFromPoint(clientX, clientY) },
        },
      }));
    },
  });
  const keyboardSensor = useSensor(KeyboardSensor);

  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const gridBoundsRef = useRef<HTMLDivElement | null>(null);
  const restrictToSchedulerGrid = useMemo<Modifier>(
    () =>
      ({ transform, draggingNodeRect }) => {
        const el = gridBoundsRef.current;
        if (!draggingNodeRect || !el) return transform;
        return restrictToBoundingRect(transform, draggingNodeRect, el.getBoundingClientRect());
      },
    [],
  );

  const modifier = restrictTo === 'window' ? restrictToWindowEdges : restrictToSchedulerGrid;
  return (
    <DndContext sensors={sensors} modifiers={[modifier]}>
      <SchedulerGrid boundsRef={gridBoundsRef} />
    </DndContext>
  );
}
